/**
 * Previous and next buttons for a row that scrolls sideways (product rows, image rows).
 * The buttons stay hidden until the row actually overflows, each is disabled at its end of the
 * row, and a click scrolls by about one view of cards: smoothly, or instantly under reduced motion.
 *
 * Markup: <fa-row-arrows data-target="ID of the scrolling element" hidden>
 *           <button data-direction="-1">…</button> <button data-direction="1">…</button>
 *         </fa-row-arrows>
 */
class FaRowArrows extends HTMLElement {
  connectedCallback() {
    const target = document.getElementById(this.dataset.target);
    // The target may wrap the element that scrolls (data-scroll-child names it)
    this.row = target?.dataset.scrollChild ? target.querySelector(target.dataset.scrollChild) : target;
    if (!this.row) return;

    this.buttons = [...this.querySelectorAll('button[data-direction]')];
    this.buttons.forEach((button) => button.addEventListener('click', () => this.#scroll(Number(button.dataset.direction))));

    this.update = this.update.bind(this);
    this.row.addEventListener('scroll', this.update, { passive: true });
    this.resizeObserver = new ResizeObserver(this.update);
    this.resizeObserver.observe(this.row);
    this.update();
  }

  disconnectedCallback() {
    this.row?.removeEventListener('scroll', this.update);
    this.resizeObserver?.disconnect();
  }

  update() {
    const { row } = this;
    const max = row.scrollWidth - row.clientWidth;
    // Rows that fit (and phones, where the row is a grid) show no buttons
    const overflows = max > 2 && getComputedStyle(row).overflowX !== 'visible';
    this.hidden = !overflows;
    if (!overflows) return;

    const [previous, next] = this.buttons;
    if (previous) previous.disabled = row.scrollLeft <= 2;
    if (next) next.disabled = row.scrollLeft >= max - 2;
  }

  #scroll(direction) {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // One view of cards, less a sliver so the next card's edge stays in sight
    const step = Math.max(this.row.clientWidth * 0.9, 200) * direction;
    this.row.scrollBy({ left: step, behavior: reduce ? 'auto' : 'smooth' });
  }
}

if (!customElements.get('fa-row-arrows')) customElements.define('fa-row-arrows', FaRowArrows);
